import { useDispatch, useSelector } from 'react-redux';
import { useRef } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
    advanceToNextQuestion,
    clearChatbotNewState,
    handleHideOrShowQuestionInNew,
    setCurrentQuestionIndex,
    setEditingQuestionIndex,
    setIsSaving,
    setQuestionAnswerInNew,
    setShowBiometricPrompt,
    setChatbotNewPhase,
    setBlocked,
    clearQuestionAnswerInNew,
} from '../../../../features/chatbotNew/chatbotNewSlice';
import { buildAnswersPayload, CC_RESPONSE_TYPE } from '../../../../features/chatbotNew/chatbotNewUtils';
import { saveSingleQuestionAnswer, getConditionalQuestions } from '../../../editAssessmentNew/EditAssessmentService';
import ExamService from '../../../../services/ExamService';
import CONSTANT from '../../../../constants/Constant';
import useChatbot from './useChatbot';
import { playAudio } from '../../../../utils/Utils';
import answerValidationService from '../../../../services/answerValidation/AnswerValidationService';

const useNewChatbotBody = () => {
    const dispatch = useDispatch();
    const { clearAndCloseChatbot } = useChatbot();
    const chatBotNewState = useSelector(state => state.chatBotNew);
    const { flatQuestions, currentQuestionIndex, testId } = chatBotNewState;

    // Ref to store stopAudio function from child component
    const stopAudioRef = useRef(null);

    const debouncedSave = useDebounce(async (answers) => {
        await saveSingleQuestionAnswer(testId, answers);
    }, 500);

    /**
     * Main answer handler — same `value` shape as EditAssessment's handleAnswerSave.
     * { answerId, answerText, question }
     */
    const handleNewChatbotAnswerSave = async (value) => {
        const responseId = value?.answerId;
        const responseValue = value?.answerText;
        const question = value?.question;
        if (!question) return;

        const { questionId, responseType } = question;
        const isCheckbox = responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX || responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT;
        const autoAdvance = responseType === CONSTANT.ELEMENT_TYPES.BUTTON;

        // Exit Workflow Check: Find if the selected response triggers an exit
        const selectedResponse = (question.responses || []).find(r => r.id === responseId);
        if (selectedResponse?.isExitWorkflow) {
            // Update the answer in state so user sees what they clicked
            dispatch(setQuestionAnswerInNew({ questionId, answerId: responseId, answerValue: responseValue }));
            
            // Save the terminal answer to the API
            const answers = buildAnswersPayload(question, responseId, responseValue);
            saveSingleQuestionAnswer(testId, answers);

            // Set blocked state
            dispatch(setBlocked(selectedResponse.exitWorkflowMessage));
            return;
        }

        // Handle conditional child questions for non-blocked paths
        handleChildQuestionInNew(question, responseId);

        if (isCheckbox) {
            // Compute new list from the current question state (value.question has the pre-toggle state)
            const currentList = question.answerList ?? [];
            const alreadySelected = currentList.includes(responseId);
            const newList = alreadySelected
                ? currentList.filter(id => id !== responseId)
                : [...currentList, responseId];

            dispatch(setQuestionAnswerInNew({ questionId, answerId: responseId, toggleCheckbox: true }));
            // Save each toggle (server stays in sync); advance happens on OK
            debouncedSave([{ AnswerList: newList, QuestionId: questionId }]);
        } else if (autoAdvance) {
            dispatch(setQuestionAnswerInNew({ questionId, answerId: responseId, answerValue: responseValue }));
            const answers = buildAnswersPayload(question, responseId, responseValue);
            dispatch(setIsSaving(true));
            await saveSingleQuestionAnswer(testId, answers);
            dispatch(setIsSaving(false));
            advanceQuestion();
        } else {
            // TEXT / NUMBER / DATE_PICKER — debounce save, advance on OK
            dispatch(setQuestionAnswerInNew({ questionId, answerId: responseId, answerValue: responseValue }));
            const answers = buildAnswersPayload(question, responseId, responseValue);
            debouncedSave(answers);
        }
    };

    /**
     * Called when the user clicks OK on text / date / checkbox questions.
     * Returns { valid: boolean, message: string } for the caller to display errors.
     */
    const handleOKForTextQuestion = async () => {
        const question = flatQuestions[currentQuestionIndex];
        if (!question) return { valid: true, message: '' };

        const { questionId, responseType, answerId, answerValue, answerList, groupId } = question;

        const isCheckbox = responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX || responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT;
        const isDropdown = responseType === CONSTANT.ELEMENT_TYPES.DROPDOWN || responseType === CONSTANT.ELEMENT_TYPES.SELECT;

        let valueToValidate;
        if (isCheckbox) {
            valueToValidate = answerList ?? [];
        } else if (isDropdown) {
            valueToValidate = answerId || 0;
        } else {
            valueToValidate = answerValue || '';
        }

        const validation = answerValidationService.validate(questionId, responseType, valueToValidate);
        if (!validation?.valid) {
            return { valid: false, message: validation?.message || 'This field is required.' };
        }

        let answers;
        if (isCheckbox) {
            answers = [{ AnswerList: answerList ?? [], QuestionId: questionId }];
        } else {
            answers = [{
                GroupId: groupId ?? CONSTANT.ASSESSMENT_GROUP.DEMOGRAPHICS.key,
                AnswerId: answerId || 0,
                AnswerTitle: answerValue || '',
                QuestionId: questionId,
            }];
        }

        dispatch(setIsSaving(true));
        await saveSingleQuestionAnswer(testId, answers);
        dispatch(setIsSaving(false));
        advanceQuestion();
        return { valid: true, message: '' };
    };

    /**
     * Show / hide conditional child questions based on the selected answer.
     */
    const handleChildQuestionInNew = (question, selectedAnswerId) => {
        const isParentCheckbox = question.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX ||
                                question.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT;

        // If it's a single-select parent and the answer is actually changing, we must clear children.
        const isChanging = !isParentCheckbox && Number(selectedAnswerId) !== Number(question.answerId);

        // Build a temporary question with the selected answerId for getConditionalQuestions
        const tempQuestion = { ...question, answerId: selectedAnswerId };
        const { childQuestionIdsOfSelectedResponse, childQuestionIdsOfNotSelectedResponse } =
            getConditionalQuestions(tempQuestion);

        // Filter out IDs that are in both arrays (don't hide what's also shown)
        const toHide = childQuestionIdsOfNotSelectedResponse.filter(
            id => !childQuestionIdsOfSelectedResponse.includes(id)
        );
        const toShow = childQuestionIdsOfSelectedResponse;

        // 1. Update visibility in Redux
        toHide.forEach(questionId => dispatch(handleHideOrShowQuestionInNew({ questionId, isDefaultHide: true })));
        toShow.forEach(questionId => dispatch(handleHideOrShowQuestionInNew({ questionId, isDefaultHide: false })));

        // 2. Clear answers from Redux for all children if the single-select parent changed
        // (handleHideOrShowQuestionInNew already handles toHide clearing in the slice)
        if (isChanging) {
            toShow.forEach(questionId => dispatch(clearQuestionAnswerInNew({ questionId })));
        }

        // 3. Clear assessments in DB for anything hidden, and also for refreshed children if parent changed
        const dbClearIds = [...toHide];
        if (isChanging) {
            dbClearIds.push(...toShow);
        }
        const uniqueClearIds = [...new Set(dbClearIds)];

        if (uniqueClearIds.length > 0) {
            ExamService.clearAssessments({ TestId: testId, QuestionIds: uniqueClearIds });
        }
    };

    /**
     * Advance to the next visible question (or complete if none left).
     */
    const advanceQuestion = () => {
        dispatch(advanceToNextQuestion());
    };

    /**
     * Edit a previously answered question.
     * @param {number} flatIndex - index in flatQuestions array
     */
    const handleEditPreviousQuestion = (flatIndex) => {
        // Clear the biometric prompt flag when editing a previous question
        // so the user can see the question they want to edit
        dispatch(setShowBiometricPrompt(false));
        dispatch(setChatbotNewPhase('active')); // Reset phase to active so questions are shown
        dispatch(setEditingQuestionIndex(flatIndex));
        dispatch(setCurrentQuestionIndex(flatIndex));
    };

    /**
     * Register stopAudio function (called by NewCurrentQuestion)
     */
    const registerStopAudio = (stopFn) => {
        stopAudioRef.current = stopFn;
    };

    /**
     * Close chatbot and clear all new chatbot state.
     */
    const handleCloseChatbotNew = () => {
        // Stop any playing audio
        if (stopAudioRef.current) {
            stopAudioRef.current();
        }
        // Clear any remaining audio from audioManager
        playAudio('remove').catch(() => {
            // Ignore errors when removing audio
        });

        dispatch(clearChatbotNewState());
        clearAndCloseChatbot();
    };

    return {
        handleNewChatbotAnswerSave,
        handleOKForTextQuestion,
        handleChildQuestionInNew,
        advanceQuestion,
        handleEditPreviousQuestion,
        handleCloseChatbotNew,
        registerStopAudio,
    };
};

export default useNewChatbotBody;
